import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface GoldPrice {
  type: string;   // e.g. 'SJC_1L', 'BTMC 9999'
  buy: number;    // VND per chỉ
  sell: number;
  updatedAt: string;
  overrideApplied?: boolean;
}

interface GoldPriceState {
  prices: GoldPrice[];
  lastUpdated: string | null;
  isConnected: boolean;
}

const initialState: GoldPriceState = {
  prices: [],
  lastUpdated: null,
  isConnected: false,
};

/** Returns true when two price entries are value-equal (type + buy + sell + overrideApplied). */
function pricesEqual(a: GoldPrice, b: GoldPrice): boolean {
  return (
    a.type === b.type &&
    a.buy === b.buy &&
    a.sell === b.sell &&
    (a.overrideApplied ?? false) === (b.overrideApplied ?? false)
  );
}

const goldPriceSlice = createSlice({
  name: 'goldPrice',
  initialState,
  reducers: {
    setPrices: (state, action: PayloadAction<GoldPrice[]>) => {
      const incoming = action.payload;
      const sameLength = incoming.length === state.prices.length;
      let unchanged = sameLength;
      if (unchanged) {
        const byType = new Map(state.prices.map((p) => [p.type, p]));
        for (const inc of incoming) {
          const existing = byType.get(inc.type);
          if (!existing || !pricesEqual(existing, inc)) {
            unchanged = false;
            break;
          }
        }
      }
      state.prices = incoming;
      if (!unchanged) state.lastUpdated = new Date().toISOString();
    },
    mergePrices: (state, action: PayloadAction<GoldPrice[]>) => {
      const byType = new Map(state.prices.map((p) => [p.type, p]));
      let changed = false;
      for (const incoming of action.payload) {
        const existing = byType.get(incoming.type);
        if (!existing || !pricesEqual(existing, incoming)) {
          byType.set(incoming.type, incoming);
          changed = true;
        }
      }
      state.prices = Array.from(byType.values());
      if (changed) state.lastUpdated = new Date().toISOString();
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
  },
});

export const { setPrices, mergePrices, setConnected } = goldPriceSlice.actions;
export default goldPriceSlice.reducer;
